import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { setPathAfterLogin } from 'src/layouts/dashboard/config-navigation';
import { AuthContext } from './auth-context';
import { getSessionToken, isValidToken, setSessionToken } from './utils';
import {
  signInMutation,
  sessionQuery,
  requestPasswordRecoverMutation,
  updatePasswordMutation,
} from './api';

const initialState = { user: null, loading: true };

const reducer = (state, action) => {
  if (action.payload?.user) setPathAfterLogin(action.payload?.user);
  if (action.type === 'INITIAL') return { loading: false, user: action.payload.user };
  if (action.type === 'LOGIN') return { ...state, user: action.payload.user };
  if (action.type === 'LOGOUT') return { ...state, user: null };
  return state;
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [getSession] = useLazyQuery(sessionQuery);
  const [signIn] = useMutation(signInMutation);
  const [requestPasswordRecover] = useMutation(requestPasswordRecoverMutation);
  const [updatePassword] = useMutation(updatePasswordMutation);

  const initialize = useCallback(async () => {
    try {
      const accessToken = getSessionToken();

      if (accessToken && isValidToken(accessToken)) {
        setSessionToken(accessToken);
        const response = await getSession();
        const session = response.data?.session;
        dispatch({ type: 'INITIAL', payload: { user: session } });
      } else {
        dispatch({ type: 'INITIAL', payload: { user: null } });
      }
    } catch (error) {
      dispatch({ type: 'INITIAL', payload: { user: null } });
    }
  }, [getSession]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(
    async (email, password) => {
      const data = { email, password };
      const response = await signIn({ variables: data });
      const { accessToken } = response.data.signIn;
      setSessionToken(accessToken);
      const userResponse = await getSession();
      const session = userResponse.data?.session;
      dispatch({ type: 'LOGIN', payload: { user: session } });
      return session;
    },
    [signIn, getSession]
  );

  const logout = useCallback(async () => {
    setSessionToken(null);
    dispatch({ type: 'LOGOUT' });
  }, []);

  const requestRecover = useCallback(
    (email) => requestPasswordRecover({ variables: { email } }),
    [requestPasswordRecover]
  );

  const changePassword = useCallback(
    (newPassword) => updatePassword({ variables: { newPassword } }),
    [updatePassword]
  );

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      initialize,
      login,
      logout,
      requestRecover,
      changePassword,
    }),
    [state.user, status, initialize, login, logout, requestRecover, changePassword]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
